import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../catalyst/dialog';
import { Button } from '../../catalyst/button';
import { Select } from '../../catalyst/select';
import { roles, type Role } from '@avisoma/shared/dist/roles.js';
import type { UserListItem } from '@avisoma/shared';
import backend from '../../services/backend';
import { useAuth } from '../../hooks/useAuth';

export function EditRoleDialog({
  user,
  open,
  onClose,
}: {
  user: UserListItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const [role, setRole] = useState<Role>(user?.role ?? 'user');

  const isSelf = user && auth.user?.id === user.id;

  const update = useMutation({
    mutationFn: () => backend.updateUserRole(user!.id, { role }).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
  });

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Change role for {user.username}</DialogTitle>
      <DialogBody>
        {/*
          The full `roles` list, `service` included: DR-3681's machine identity is given to an
          existing account from here, so it stays visible and manageable instead of needing SQL.
          It is labelled as a machine role because it grants no human reach — a service token is
          judged by Brain's route allowlist, not by the role gates the other options carry.
        */}
        <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {roles.map((r) => (
            <option key={r} value={r} disabled={!!(isSelf && r !== 'admin')}>
              {r === 'service' ? 'service — machine identity (route allowlist)' : r}
              {isSelf && r !== 'admin' ? ' — you cannot demote yourself' : ''}
            </option>
          ))}
        </Select>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="blue"
          onClick={() => update.mutate()}
          disabled={update.isPending || role === user.role}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
